import DocumentMeta from 'react-document-meta';
import './tokens.css';
import styles from './login.module.css';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../header/index';
import { FaEye } from "react-icons/fa";
import ErrorMessage from '../../package/error/index';
import { getFirestore, updateDoc, doc, arrayRemove, setDoc, collection, where, query, getDocs, limit, or } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import DotLoading from './loading';
import { defaultInitialDataMap } from 'src/type';
import { SlidingChipSelector } from './items/chipSelector';
import AppPreview from './items/appPreview';

const AuthLogin: React.FC = () => {

  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  const [ loading, setLoading ] = useState<LoadingState>("idle");
  type LoadingState = "idle" | "loading" | "success" | "error" | "wrongTab";

  const [ errorMessage, setErrorMessage ] = useState('');
  const [ email, setEmail ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ passwordEye, setPasswordEye ] = useState(false);

  const [ selectedIndex, setSelectedIndex ] = useState<number>(0);

  function check() {

    setErrorMessage('');

    if (email.length <= 7) {
      setErrorMessage('メールアドレスを入力してください。');
    } else if (password.length <= 7) {
      setErrorMessage('パスワードを8文字以上入力してください。');
    } else {
      Register();
    }
  }

  const Register = async () => {
    setLoading("loading");

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (selectedIndex === 0) {
        checkData(user.uid, email);
      } else {
        schoolLogin(user.uid, email);
      }
    } catch (error: any) {
      setLoading("error");
      switch (error.code) {
        case 'auth/invalid-email':
          setErrorMessage('メールアドレスの形式が正しくありません。');
          break;
        case 'auth/invalid-credential':
          setErrorMessage('メールアドレスまたはパスワードが正しくありません。');
          break;
        case 'auth/user-disabled':
          setErrorMessage('このアカウントは無効化されています。');
          break;
        case 'auth/user-not-found':
          setErrorMessage('指定されたメールアドレスのアカウントは存在しません。');
          break;
        case 'auth/wrong-password':
          setErrorMessage('パスワードが間違っています。');
          break;
        case 'auth/too-many-requests':
          setErrorMessage('ログイン試行が多すぎます。しばらく時間を置いてから再度お試しください。');
          break;
        default:
          setErrorMessage('エラー詳細が不明です。担当者にご連絡ください。');
      }
    }
  };

  async function checkData(uid: string, email: string) {
    try {
      console.log('[debug] ① companies owner query 開始');
      const companiesRef = collection(db, "companies");
      const q = query(companiesRef, where("owner", "array-contains", uid), limit(1));
      const querySnapshot = await getDocs(q);
      console.log('[debug] ① companies owner query 成功', querySnapshot.size);

      const searchCode = String(Math.floor(10000 + Math.random() * 90000));

      let inviteData = null;
      if (!querySnapshot.empty) {
        const companyDoc = querySnapshot.docs[0];
        const companydata = companyDoc.data();
        inviteData = companydata.invite ?? null;
      }

      if (querySnapshot.empty) {
        // 会社オーナーでなければ、companies.judgeUids にログインuidが含まれる審査員か確認する
        const judgeQ = query(collection(db, 'companies'), where('judgeUids', 'array-contains', uid), limit(1));
        const judgeSnap = await getDocs(judgeQ);
        if (!judgeSnap.empty) {
          setLoading('success');
          navigate(auth.currentUser?.emailVerified ? '/service/evaluation' : '/company/confirm');
          return;
        }

        setLoading("error");
        setErrorMessage('メールアドレスに対応する企業情報がないため、LINE@より担当者にご連絡ください。');
        return;
      }

      const companyId = querySnapshot.docs[0].id;

      if (Array.isArray(inviteData) && inviteData.includes(email)) {

        console.log('[debug] ② companies.invite arrayRemove 開始');
        const docRef = doc(db, "companies", companyId);
        await updateDoc(docRef, {
          invite: arrayRemove(email),
        });
        console.log('[debug] ② companies.invite arrayRemove 成功');

        const newDocId = companyId + "_2027";
        const newDocRef = doc(db, "recruits", newDocId);

        console.log('[debug] ③ recruits setDoc(defaultInitialDataMap) 開始', newDocId);
        await setDoc(newDocRef, defaultInitialDataMap);
        console.log('[debug] ③ recruits setDoc 成功');

        console.log('[debug] ④ recruits companyId updateDoc 開始');
        await updateDoc(newDocRef, {
          companyId: companyId,
          searchCode: searchCode,
        });
        console.log('[debug] ④ recruits companyId updateDoc 成功');

        loginCheck();
      } else {
        loginCheck();
      }
    } catch (e) {
      console.error(e); // 一時的なデバッグ用ログ。原因特定後に削除すること。
      setLoading("error");
      setErrorMessage('ログインが出来ませんでした。担当者にご連絡ください。');
    }
  }

  async function loginCheck() {
    const user = auth.currentUser;
    if (user) {
      if (user.emailVerified) {
        navigate(`/service/manual/plan`);
        setLoading("success");
      } else {
        setLoading("success");
        navigate('/company/confirm');
      }
    } else {
      setLoading("error");
      setErrorMessage('ログインが出来ませんでした。担当者にご連絡ください。');
    }
  }

  async function schoolLogin(uid: string, email: string) {
    try {
      const companiesRef = collection(db, "schools");
      // schools.owner は通常は単一uid文字列だが、運営メンバーにもアクセスを許可した
      // 学校だけ配列([元のuid, 追加のuid, ...])になっているため両方にマッチさせる。
      const q = query(
        companiesRef,
        or(where("owner", "==", uid), where("owner", "array-contains", uid)),
        limit(1)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        schoolLoginCheck();
      } else {
        setLoading("wrongTab");
      }
    } catch {
      setLoading("error");
      setErrorMessage('ログインが出来ませんでした。担当者にご連絡ください。');
    }
  }

  async function schoolLoginCheck() {
    const user = auth.currentUser;
    if (user) {
      navigate(`/school/service`);
      setLoading("success");
    } else {
      setLoading("error");
      setErrorMessage('ログインが出来ませんでした。担当者にご連絡ください。');
    }
  }

  const meta = {
    title: "ログイン | 株式会社memorii（memorii works）",
    description: "株式会社memoriiが提供する『memorii works』のログインページです。美容室の方はこちらからログインできます。",
    canonical: "https://memorii-works.com/company/login",
    meta: { charset: "utf-8", name: { robots: 'noindex,nofollow,noarchive,noimageindex' } }
  };

  const isBusiness = selectedIndex === 0;

  return (
    <DocumentMeta {...meta}>
      <div className={styles.page}>

        <Header />

        <main className={styles.wrap}>
          <div className={styles.card}>

            <section className={styles.brand}>
              <div className={styles.brandInner}>
                <p className={styles.brandTagline}>
                  美容業界の新卒採用を、<br />
                  ひとつの仕組みに。
                </p>

                <AppPreview />
              </div>
            </section>

            <section className={styles.panel}>

              {loading === "loading" && (
                <div className={styles.state}>
                  <DotLoading text="ログイン中" />
                </div>
              )}

              {loading === "success" && (
                <div className={styles.state}>
                  <p className={styles.stateText}>ログインしました。画面を移動しています…</p>
                </div>
              )}

              {loading === "wrongTab" && (
                <div className={styles.state}>
                  <p className={styles.stateText}>
                    こちらは学校様用のログイン画面です。求人票の提出を行う場合は、タブを「事業者様」に切り替えてください。
                  </p>
                  <button type="button" className={styles.linkBtn} onClick={() => setLoading("idle")}>
                    ログイン画面に戻る
                  </button>
                </div>
              )}

              {(loading === "idle" || loading === "error") && (
                <form
                  className={styles.form}
                  onSubmit={(e) => { e.preventDefault(); check(); }}
                >
                  <h1 className={styles.title}>ログイン</h1>

                  <div className={styles.tabs}>
                    <p className={styles.tabsLabel}>アカウント種別</p>
                    <SlidingChipSelector
                      initialIndex={selectedIndex}
                      onSelected={(index) => setSelectedIndex(index)}
                    />
                  </div>

                  {errorMessage && <ErrorMessage message={errorMessage} />}

                  <div className={styles.field}>
                    <label htmlFor="login-email" className={styles.label}>メールアドレス</label>
                    <input
                      id="login-email"
                      className={styles.input}
                      type="email"
                      autoComplete="email"
                      placeholder="memorii@trankllc.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="login-password" className={styles.label}>パスワード</label>
                    <div className={styles.inputWrap}>
                      <input
                        id="login-password"
                        className={styles.input}
                        type={passwordEye ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="8文字以上"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        className={styles.eye}
                        aria-label={passwordEye ? 'パスワードを隠す' : 'パスワードを表示'}
                        aria-pressed={passwordEye}
                        onClick={() => setPasswordEye(!passwordEye)}
                      >
                        <FaEye />
                      </button>
                    </div>
                  </div>

                  <button type="submit" className={styles.submit}>ログインする</button>

                  {isBusiness && (
                    <div className={styles.links}>
                      <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/signin')}>
                        アカウントをお持ちではない方はこちら
                      </button>
                      <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/reset')}>
                        パスワードをお忘れの方はこちら
                      </button>
                    </div>
                  )}
                </form>
              )}

            </section>

          </div>
        </main>

      </div>
    </DocumentMeta>
  );
}

export default AuthLogin;
